import React from "react";
import styled from "styled-components";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import { Card, CardLabel } from "../ui/RecordLayout";

/**
 * The ball this sheet was drilled for, offered rather than assumed.
 *
 * Most shop forms print a maker/model/serial table, and everything in it used
 * to be flattened into the notes where nothing could use it -- even though a
 * drilled ball is its own record against the bowler, tied to the very sheet
 * being created here.
 *
 * It is a decision, never a side effect. A spec sheet is often a re-drill or
 * a page of measurements, and plenty of forms print the table and leave it
 * blank: one of the first sheets read in production did exactly that. So this
 * appears only when the sheet gave us something, starts ticked because a
 * named ball on a sheet is nearly always real, and unticks in one tap. A ball
 * that is missed gets typed into the arsenal; a ball invented from a blank
 * table sits there unnoticed until the arsenal is full of them.
 *
 * Shaped like the arsenal card it is about to become rather than like a form,
 * for the same reason the measurements above it are chips.
 */

const Tile = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 52px;
  margin-bottom: 0;
  padding: 4px;
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) => (p.$on ? "#ffffff" : "#f7f8fa")};
`;

const Orb = styled.span`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: radial-gradient(circle at 32% 28%, #6b74c9, #1b2a72 62%, #0d1440);
  opacity: ${(p) => (p.$on ? 1 : 0.35)};
`;

const Meta = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const Named = styled.span`
  display: block;
  font-size: 12.5px;
  font-weight: 700;
  color: #00187e;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Detail = styled.span`
  display: block;
  font-size: 10px;
  color: #6b7280;
  letter-spacing: 0.03em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
  margin-top: 10px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 0;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
`;

const Value = styled(Form.Control)`
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 44px;
  font-size: 13px;
`;

// Free text on purpose, unlike the measurements in the header: a ball name or
// a layout is whatever the sheet says, with no list to be wrong about.
const EDITABLE = [
  ["manufacturer", "specsheetScan.ball.manufacturer"],
  ["ball_name", "specsheetScan.ball.name"],
  ["serial_number", "specsheetScan.ball.serial"],
  ["weight", "specsheetScan.ball.weight"],
  ["layout", "specsheetScan.ball.layout"],
  ["surface_preparation", "specsheetScan.ball.surface"],
];

const ScanBallCard = ({ ball, checked, onCheck, onChange }) => {
  const { t } = useTranslation();

  // Shown whenever the sheet gave us anything at all, not only while a name
  // survives: gating on the name would make the card vanish the moment
  // someone cleared that box to retype it, with no way back. The name is
  // still what decides whether a ball is created -- that gate lives with the
  // decision, not with the display.
  const anything = ball && Object.keys(ball).some((k) => ball[k]);
  if (!anything) return null;

  const title =
    [ball.manufacturer, ball.ball_name].filter(Boolean).join(" ").trim() ||
    t("specsheetScan.ball.unnamed");

  const detail = [ball.weight, ball.serial_number, ball.layout]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card>
      <CardLabel>{t("specsheetScan.arsenal")}</CardLabel>
      <Tile $on={checked}>
        <Form.Check
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheck(e.target.checked)}
        />
        <Orb $on={checked} />
        <Meta>
          <Named>{title}</Named>
          {detail ? <Detail>{detail}</Detail> : null}
        </Meta>
      </Tile>
      {checked && (
        <Fields>
          {EDITABLE.map(([key, labelKey]) => (
            <Field key={key}>
              <FieldLabel>{t(labelKey)}</FieldLabel>
              <Value
                value={ball[key] || ""}
                onChange={(e) => onChange({ ...ball, [key]: e.target.value })}
              />
            </Field>
          ))}
        </Fields>
      )}
    </Card>
  );
};

export default ScanBallCard;
